import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, Storefront, ChartLineUp, UsersThree, ShieldCheck } from "@phosphor-icons/react";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import SmsConsentCheckbox from "@/components/shared/SmsConsentCheckbox";
import { submitMerchantApply } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/lib/useSeo";

const BENEFITS = [
  { icon: UsersThree, title: "Access new customers", desc: "Thousands of monthly orders across T&T and the Caribbean." },
  { icon: ChartLineUp, title: "Grow visibility", desc: "Premium placements, promos and customer notifications." },
  { icon: Storefront, title: "Merchant dashboard", desc: "Orders, menus, analytics and payouts in one place." },
  { icon: ShieldCheck, title: "Done-for-you delivery", desc: "We handle drivers, routing and customer support." },
];

const CATEGORIES = [
  { v: "delivery_partner", l: "Delivery Partner (restaurant / store / e-commerce)" },
  { v: "handyman", l: "Handyman / Trades Professional" },
  { v: "cleaning", l: "Cleaning Service" },
  { v: "tech_support", l: "Tech Support / IT Professional" },
  { v: "events", l: "Events Vendor (bartender, decor, security, crew)" },
  { v: "retail", l: "Retail / Online Business" },
  { v: "other", l: "Other" },
];

const INIT = { business_name: "", contact_person: "", email: "", phone: "", category: "delivery_partner", location: "", monthly_orders_estimate: "", notes: "", sms_consent: false };

export default function Partners() {
  const [form, setForm] = useState(INIT);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useSeo({
    title: "Partner With Us",
    description: "Plug your delivery business, handyman crew, cleaning service, tech support practice or event vendor business into IslandHop's Caribbean network.",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitMerchantApply(form);
      toast.success(res.message);
      setSubmitted(true);
      setForm(INIT);
    } catch {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="partners-page">
      <section className="relative pt-20 lg:pt-28 pb-16 border-b border-ih-line overflow-hidden">
        <div className="absolute inset-0 ih-grid-bg opacity-40 [mask-image:linear-gradient(180deg,#000,transparent)]" />
        <div className="absolute -top-32 -left-32 w-[600px] h-[600px] ih-radial-blue" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 space-y-7 max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-6 max-w-3xl">
            <SectionEyebrow label="Partner with IslandHop" color="electric" />
            <h1 className="font-display font-black text-5xl lg:text-7xl text-white leading-[0.95] text-balance">
              Deliver more.<br />
              <span className="text-white/40">Sell more.</span> <span className="text-ih-electric">Grow faster.</span>
            </h1>
            <p className="text-white/70 text-lg max-w-xl leading-relaxed">
              Plug your business into the Caribbean&apos;s next-gen delivery infrastructure.
              Transparent <span className="text-white">10–25%</span> commission, tools to scale and zero setup hassle.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#apply" data-testid="partners-hero-cta" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-ih-electric text-white font-semibold hover:scale-[1.02] transition ih-shadow-blue">
                Apply to partner
              </a>
              <a href="#benefits" className="text-sm text-white/60 hover:text-white transition underline underline-offset-4 decoration-ih-line2">
                See partner benefits
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="benefits" className="py-20">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="bg-ih-surface border border-ih-line rounded-2xl p-7 hover:border-ih-electric/40 transition">
                <Icon weight="duotone" size={26} className="text-ih-electric mb-4" />
                <p className="font-display font-bold text-lg text-white mb-1.5">{b.title}</p>
                <p className="text-white/55 text-sm leading-relaxed">{b.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section id="apply" className="py-20 border-t border-ih-line">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-5 lg:sticky lg:top-24 self-start">
            <SectionEyebrow label="Merchant application" color="electric" />
            <h2 className="font-display font-extrabold text-4xl text-white leading-[1.05]">
              Let&apos;s get your <br />
              business <span className="text-ih-electric">listed.</span>
            </h2>
            <p className="text-white/60 leading-relaxed">
              Tell us about your business and our partnerships team will be in touch within 2 business days.
            </p>
            <ul className="space-y-2 pt-2 text-sm text-white/65">
              <li>✓ Free to apply</li>
              <li>✓ 10–25% commission depending on category</li>
              <li>✓ Optional subscription plans for premium tools</li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <SuccessCard onReset={() => setSubmitted(false)} />
            ) : (
              <form onSubmit={onSubmit} data-testid="merchant-apply-form" className="bg-ih-surface border border-ih-line rounded-3xl p-8 lg:p-10 space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Business name *">
                    <input data-testid="merchant-business" required value={form.business_name} onChange={(e) => update("business_name", e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Contact person *">
                    <input data-testid="merchant-contact" required value={form.contact_person} onChange={(e) => update("contact_person", e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Email *">
                    <input type="email" data-testid="merchant-email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Phone *">
                    <input data-testid="merchant-phone" required value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} placeholder="+1 868 ..." />
                  </Field>
                  <Field label="Category *">
                    <select data-testid="merchant-category" value={form.category} onChange={(e) => update("category", e.target.value)} className={inputCls}>
                      {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                    </select>
                  </Field>
                  <Field label="Location *">
                    <input data-testid="merchant-location" required value={form.location} onChange={(e) => update("location", e.target.value)} className={inputCls} placeholder="San Fernando" />
                  </Field>
                </div>
                <Field label="Estimated monthly orders">
                  <input data-testid="merchant-orders" value={form.monthly_orders_estimate} onChange={(e) => update("monthly_orders_estimate", e.target.value)} className={inputCls} placeholder="e.g. 200–500" />
                </Field>
                <Field label="Tell us about your business">
                  <textarea data-testid="merchant-notes" rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} className={`${inputCls} resize-none`} />
                </Field>

                <SmsConsentCheckbox
                  checked={form.sms_consent}
                  onChange={(v) => update("sms_consent", v)}
                  accent="electric"
                  testId="merchant-sms-consent"
                />

                <button type="submit" disabled={loading} data-testid="merchant-submit" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-ih-electric text-white font-semibold hover:scale-[1.02] disabled:opacity-60 transition w-full sm:w-auto justify-center">
                  {loading ? "Submitting..." : "Submit application"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-black/40 border border-ih-line2 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-ih-electric transition text-sm";

const Field = ({ label, children }) => (
  <label className="block space-y-1.5">
    <span className="text-xs uppercase tracking-[0.16em] text-white/50">{label}</span>
    {children}
  </label>
);

const SuccessCard = ({ onReset }) => (
  <div data-testid="merchant-success" className="bg-ih-surface border border-ih-electric/40 rounded-3xl p-10 lg:p-14 text-center ih-shadow-blue">
    <CheckCircle weight="duotone" size={56} className="mx-auto text-ih-electric mb-5" />
    <h3 className="font-display font-extrabold text-3xl text-white mb-3">Partnership request received</h3>
    <p className="text-white/65 max-w-md mx-auto leading-relaxed">
      Our partnerships team will reach out within 2 business days to start onboarding your business.
    </p>
    <button onClick={onReset} className="mt-6 text-sm text-ih-electric underline underline-offset-4">Submit another</button>
  </div>
);
